import React, { useMemo } from "react";
import moment from "moment";
import { Link as RouterLink } from "react-router-dom";

import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";

import Grid from "@material-ui/core/Grid";
import Link from "@material-ui/core/Link";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";

import ChartCard from "./ChartCard";
import ChartTooltip from "./ChartTooltip";
import KpiTile from "./KpiTile";
import {
  useChartTheme,
  formatCurrency,
  formatCompactCurrency,
  formatNumber,
  formatPeriod,
} from "./dashboardUtils";

// Each payout type owns a fixed palette slot, so a type keeps its colour
// whichever other types happen to have data in the period.
const PAYOUT_TYPES = [
  { key: "package", label: "Hotel packages", slot: 0 },
  { key: "condo", label: "Condos", slot: 1 },
  { key: "wc_upgrade", label: "WooCommerce upgrades", slot: 2 },
  { key: "custom", label: "Custom", slot: 3 },
];

const PAYOUT_TYPE_LABELS = PAYOUT_TYPES.reduce(
  (acc, t) => ({ ...acc, [t.key]: t.label }),
  {}
);

const payoutLink = (payout) =>
  payout.payment_type === "wc_upgrade"
    ? `/sales/woocommerce-payouts/${payout.payment_id}`
    : `/sales/payouts/${payout.payment_id}`;

const CHART_HEIGHT = 260;

const PayoutsSection = ({ payouts, granularity }) => {
  const chartTheme = useChartTheme();
  const { current, previous, over_time: overTime, awaiting, recent } = payouts;

  const activeTypes = useMemo(
    () =>
      PAYOUT_TYPES.filter((t) =>
        (overTime || []).some((bucket) => bucket[t.key] > 0)
      ),
    [overTime]
  );

  const chart = (
    <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
      <BarChart
        data={overTime}
        margin={{ top: 8, right: 8, left: 0, bottom: 0 }}
      >
        <CartesianGrid vertical={false} stroke={chartTheme.grid} />
        <XAxis
          dataKey="period"
          stroke={chartTheme.grid}
          tick={{ fill: chartTheme.muted, fontSize: 12 }}
          tickLine={false}
          tickFormatter={(p) => formatPeriod(p, granularity)}
          minTickGap={16}
        />
        <YAxis
          stroke={chartTheme.grid}
          tick={{ fill: chartTheme.muted, fontSize: 12 }}
          tickLine={false}
          axisLine={false}
          width={64}
          tickFormatter={formatCompactCurrency}
        />
        <Tooltip
          cursor={{ fill: chartTheme.grid }}
          content={
            <ChartTooltip
              labelFormatter={(p) =>
                formatPeriod(p, granularity, { long: true })
              }
              formatter={formatCurrency}
            />
          }
        />
        {activeTypes.length > 1 && (
          <Legend
            iconType="square"
            wrapperStyle={{ fontSize: 13 }}
            // Legend text stays in text ink; the icon carries the series colour.
            formatter={(value) => (
              <span style={{ color: chartTheme.text }}>{value}</span>
            )}
          />
        )}
        {activeTypes.map((t, i) => (
          <Bar
            key={t.key}
            dataKey={t.key}
            name={t.label}
            stackId="payouts"
            fill={chartTheme.series[t.slot]}
            // 2px surface gap between stacked segments
            stroke={chartTheme.surface}
            strokeWidth={activeTypes.length > 1 ? 1 : 0}
            radius={i === activeTypes.length - 1 ? [4, 4, 0, 0] : 0}
            maxBarSize={32}
          />
        ))}
      </BarChart>
    </ResponsiveContainer>
  );

  const recentTable = (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Payout</TableCell>
            <TableCell>Date</TableCell>
            <TableCell>Payee</TableCell>
            <TableCell>Type</TableCell>
            <TableCell align="right">Commission</TableCell>
            <TableCell align="right">Paid</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {(recent || []).map((p) => (
            <TableRow key={p.payment_id} hover>
              <TableCell>
                <Link component={RouterLink} to={payoutLink(p)}>
                  {`#${p.payment_id}`}
                </Link>
              </TableCell>
              <TableCell style={{ whiteSpace: "nowrap" }}>
                {moment(p.created_at).format("MMM D, YYYY")}
              </TableCell>
              <TableCell>{p.stripe_account_name || "—"}</TableCell>
              <TableCell>
                {PAYOUT_TYPE_LABELS[p.payment_type] || p.payment_type}
              </TableCell>
              <TableCell align="right">
                {formatCurrency(p.commission_amount)}
              </TableCell>
              <TableCell align="right">
                {formatCurrency(p.total_paid)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  const overTimeTable = (
    <TableContainer style={{ maxHeight: CHART_HEIGHT + 40 }}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell>Period</TableCell>
            {PAYOUT_TYPES.map((t) => (
              <TableCell key={t.key} align="right">
                {t.label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {(overTime || []).map((row) => (
            <TableRow key={row.period}>
              <TableCell>
                {formatPeriod(row.period, granularity, { long: true })}
              </TableCell>
              {PAYOUT_TYPES.map((t) => (
                <TableCell key={t.key} align="right">
                  {formatCurrency(row[t.key])}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} sm={4}>
        <KpiTile
          label="Paid out to properties"
          value={formatCurrency(current.total_paid)}
          current={current.total_paid}
          previous={previous.total_paid}
          hint={`${formatNumber(current.count)} payouts in this period`}
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <KpiTile
          label="Commission retained"
          value={formatCurrency(current.commission)}
          current={current.commission}
          previous={previous.commission}
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <KpiTile
          label="Awaiting payout"
          value={formatCurrency(awaiting.collected)}
          hint={
            <>
              {`${formatNumber(
                awaiting.orders
              )} completed stays not paid out yet · `}
              <Link component={RouterLink} to="/sales/payouts/new">
                Create payout
              </Link>
            </>
          }
        />
      </Grid>
      <Grid item xs={12} lg={6}>
        <ChartCard
          title="Payouts over time"
          subtitle="Completed transfers, by payout date"
          isEmpty={activeTypes.length === 0}
          chart={chart}
          table={overTimeTable}
        />
      </Grid>
      <Grid item xs={12} lg={6}>
        <ChartCard
          title="Recent payouts"
          subtitle="Latest completed transfers, any date"
          isEmpty={!recent || recent.length === 0}
          table={recentTable}
        />
      </Grid>
    </Grid>
  );
};

export default PayoutsSection;
